/**
 * Type Validation Service
 * Handles all type validation logic for node connections
 */

import { 
  EnhancedNodeType, 
  ValidationResult, 
  EnhancedSocketInfo,
  TypeUtils
} from '../types/enhanced-types';
import { TypeCompatibilityChecker } from '../constants/TypeCompatibilityMatrix';

export class TypeValidationService {
  private static instance: TypeValidationService;

  public static getInstance(): TypeValidationService {
    if (!TypeValidationService.instance) {
      TypeValidationService.instance = new TypeValidationService();
    }
    return TypeValidationService.instance;
  }

  /**
   * Validate a connection between two socket types
   */
  validateConnection(sourceType: EnhancedNodeType, targetType: EnhancedNodeType): ValidationResult {
    // Same type - always valid
    if (sourceType === targetType) {
      return {
        isValid: true,
        category: 'valid',
        message: `${TypeUtils.getTypeDisplayName(sourceType)} types match perfectly`
      };
    }

    // Check direct compatibility
    if (TypeCompatibilityChecker.isDirectlyCompatible(sourceType, targetType)) {
      return {
        isValid: true,
        category: 'valid',
        message: TypeCompatibilityChecker.getCompatibilityMessage(sourceType, targetType)
      };
    }

    // Check for possible type conversion
    const conversion = TypeCompatibilityChecker.getTypeConversion(sourceType, targetType);
    if (conversion) {
      const category = TypeCompatibilityChecker.getCompatibilityCategory(sourceType, targetType);
      
      return {
        isValid: category !== 'invalid',
        category,
        message: TypeCompatibilityChecker.getCompatibilityMessage(sourceType, targetType),
        autoConversion: {
          possible: true,
          targetType,
          accuracy: conversion.confidence
        }
      };
    }

    // Check category compatibility (SIGNAL vs DATA)
    if (!TypeUtils.sameCategory(sourceType, targetType)) {
      const sourceCategory = TypeUtils.getTypeCategory(sourceType);
      const targetCategory = TypeUtils.getTypeCategory(targetType);
      
      return {
        isValid: false,
        category: 'invalid',
        message: `Cannot connect ${sourceCategory} outputs to ${targetCategory} inputs`,
        suggestedTypes: this.getSuggestedTypes(sourceType, targetCategory)
      };
    }

    // Types are incompatible
    return {
      isValid: false,
      category: 'invalid',
      message: TypeCompatibilityChecker.getCompatibilityMessage(sourceType, targetType),
      suggestedTypes: TypeCompatibilityChecker.getCompatibleTypes(sourceType)
    };
  }

  /**
   * Validate a connection between two sockets (full socket info)
   */
  validateSocketConnection(sourceSocket: EnhancedSocketInfo, targetSocket: EnhancedSocketInfo): ValidationResult {
    // Check direction compatibility (output → input)
    if (sourceSocket.direction === targetSocket.direction) {
      return {
        isValid: false,
        category: 'invalid',
        message: `Cannot connect ${sourceSocket.direction} to ${targetSocket.direction}`
      };
    }

    // Ensure we're connecting output → input
    const outputSocket = sourceSocket.direction === 'output' ? sourceSocket : targetSocket;
    const inputSocket = sourceSocket.direction === 'input' ? sourceSocket : targetSocket;

    // Check type compatibility
    const typeValidation = this.validateConnection(outputSocket.type, inputSocket.type);

    // Check multi-type inputs
    if (inputSocket.acceptedTypes && inputSocket.acceptedTypes.length > 0) {
      const isAccepted = inputSocket.acceptedTypes.some(acceptedType => 
        TypeCompatibilityChecker.isCompatible(outputSocket.type, acceptedType)
      );

      if (isAccepted) {
        return {
          isValid: true,
          category: 'valid',
          message: `${TypeUtils.getTypeDisplayName(outputSocket.type)} is accepted by multi-type input`
        };
      } else {
        const acceptedTypeNames = inputSocket.acceptedTypes
          .map(type => TypeUtils.getTypeDisplayName(type))
          .join(', ');
        
        return {
          isValid: false,
          category: 'invalid',
          message: `This input only accepts: ${acceptedTypeNames}`,
          suggestedTypes: inputSocket.acceptedTypes
        };
      }
    }

    return typeValidation;
  }

  /**
   * Get suggested compatible types for a given type and target category
   */
  private getSuggestedTypes(sourceType: EnhancedNodeType, targetCategory: string): EnhancedNodeType[] {
    if (targetCategory === 'SIGNAL') {
      return [SignalType.TRIGGER, SignalType.EVENT, SignalType.FLOW];
    } else if (targetCategory === 'DATA') {
      return [DataType.TEXT, DataType.NUMBER, DataType.BOOLEAN, DataType.DATA_ANY];
    }
    return [];
  }

  /**
   * Check if a connection would create a type mismatch warning
   */
  hasTypeWarning(sourceType: EnhancedNodeType, targetType: EnhancedNodeType): boolean {
    const validation = this.validateConnection(sourceType, targetType);
    return validation.category === 'warning';
  }

  /**
   * Get all compatible target types for a source type
   */
  getCompatibleTargetTypes(sourceType: EnhancedNodeType): EnhancedNodeType[] {
    return TypeCompatibilityChecker.getCompatibleTypes(sourceType);
  }

  /**
   * Check if automatic type conversion is available
   */
  canAutoConvert(sourceType: EnhancedNodeType, targetType: EnhancedNodeType): boolean {
    return TypeCompatibilityChecker.canAutoConvert(sourceType, targetType);
  }

  /**
   * Get type conversion details
   */
  getConversionDetails(sourceType: EnhancedNodeType, targetType: EnhancedNodeType) {
    return TypeCompatibilityChecker.getTypeConversion(sourceType, targetType);
  }

  /**
   * Validate multiple connections at once (for bulk validation)
   */
  validateMultipleConnections(connections: Array<{sourceType: EnhancedNodeType, targetType: EnhancedNodeType}>): ValidationResult[] {
    return connections.map(({ sourceType, targetType }) => 
      this.validateConnection(sourceType, targetType)
    );
  }

  /**
   * Get validation summary for a set of connections
   */
  getValidationSummary(validationResults: ValidationResult[]): {
    totalConnections: number;
    validConnections: number;
    warningConnections: number;
    invalidConnections: number;
    hasErrors: boolean;
  } {
    return {
      totalConnections: validationResults.length,
      validConnections: validationResults.filter(r => r.category === 'valid').length,
      warningConnections: validationResults.filter(r => r.category === 'warning').length,
      invalidConnections: validationResults.filter(r => r.category === 'invalid').length,
      hasErrors: validationResults.some(r => !r.isValid)
    };
  }
}

// Singleton instance
export const typeValidationService = TypeValidationService.getInstance();

// Import shortcuts for convenience
import { SignalType, DataType } from '../types/enhanced-types';
export { SignalType, DataType };

export default TypeValidationService;